import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Info as InfoIcon,
  LocalHospital as MedicalIcon,
  Timeline as PrognosisIcon,
  Science as CausesIcon,
} from '@mui/icons-material';
import { gekkoApi, GekkoApiError } from '../utils/gekkoApi';
import type { Disease } from '../types/api';

export interface DiseaseModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Function to call when the modal should be closed */
  onClose: () => void;
  /** The disease ID to display information for */
  diseaseId: number;
  /** Optional disease name to show in title while loading */
  diseaseName?: string;
}

const DiseaseModal: React.FC<DiseaseModalProps> = ({ 
  open, 
  onClose, 
  diseaseId, 
  diseaseName 
}) => {
  const [diseaseInfo, setDiseaseInfo] = React.useState<Disease | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Fetch disease information when modal opens or disease ID changes
  React.useEffect(() => {
    const fetchDiseaseInfo = async () => {
      if (!diseaseId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const disease = await gekkoApi.getDisease(diseaseId);
        setDiseaseInfo(disease);
      } catch (err) {
        let errorMessage = 'Failed to load disease information. Please try again.';
        
        if (err instanceof GekkoApiError) {
          if (err.statusCode === 401) {
            errorMessage = 'Authentication required. Please log in again.';
          } else if (err.statusCode === 404) {
            errorMessage = 'Disease not found.';
          } else if (err.statusCode === 403) {
            errorMessage = 'Access denied. You may not have permission to view this disease.';
          } else {
            errorMessage = err.message;
          }
        }
        
        setError(errorMessage);
        console.error('Error fetching disease info:', err);
      } finally {
        setLoading(false);
      }
    };

    if (open && diseaseId) {
      void fetchDiseaseInfo();
    }
  }, [open, diseaseId]);

  const handleClose = () => {
    setDiseaseInfo(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '90vh' }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MedicalIcon color="primary" />
            <Typography variant="h6">
              {diseaseInfo?.name || diseaseName || `Disease ${diseaseId}`}
            </Typography>
          </Box>
          <Button
            onClick={handleClose}
            size="small"
            sx={{ minWidth: 'auto', p: 0.5 }}
          >
            <CloseIcon />
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {diseaseInfo && !loading && (
          <Box sx={{ gap: 2, display: 'flex', flexDirection: 'column' }}>
            {/* Basic Information */}
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <InfoIcon fontSize="small" />
                  Basic Information
                </Typography>
                <Typography variant="body2" paragraph>
                  {diseaseInfo.info || 'No description available'}
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                  {diseaseInfo.can_occur_without_symptoms && (
                    <Chip 
                      label="Can occur without symptoms" 
                      size="small" 
                      color="warning"
                      variant="outlined" 
                    />
                  )}
                  {diseaseInfo.has_treatments && (
                    <Chip 
                      label="Has treatments" 
                      size="small" 
                      color="success"
                      variant="outlined" 
                    />
                  )}
                  {diseaseInfo.area_specified && (
                    <Chip 
                      label="Area specific" 
                      size="small" 
                      color="info"
                      variant="outlined" 
                    />
                  )}
                </Box>
              </CardContent>
            </Card>

            {/* Confirmation Methods */}
            {diseaseInfo.how_to_confirm && (
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    How to Confirm Diagnosis
                  </Typography>
                  <Typography variant="body2">
                    {diseaseInfo.how_to_confirm}
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/* Exclusion Methods */}
            {diseaseInfo.how_to_exclude && (
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    How to Exclude Disease
                  </Typography>
                  <Typography variant="body2">
                    {diseaseInfo.how_to_exclude}
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/* Treatment Information */}
            {diseaseInfo.treatment_information && (
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Treatment Information
                  </Typography>
                  <Typography variant="body2">
                    {diseaseInfo.treatment_information}
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/* Follow-up Information */}
            {diseaseInfo.follow_up && (
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PrognosisIcon fontSize="small" />
                    Follow-up & Monitoring
                  </Typography>
                  <Typography variant="body2">
                    {diseaseInfo.follow_up}
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/* Disease Causes */}
            {diseaseInfo.disease_causes && diseaseInfo.disease_causes.length > 0 && (
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CausesIcon fontSize="small" />
                    Causes & Risk Factors
                  </Typography>
                  <List dense>
                    {diseaseInfo.disease_causes.map((cause) => (
                      <ListItem key={cause.id}>
                        <ListItemText primary={cause.name} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            )}

            {/* Sources */}
            {diseaseInfo.sources && diseaseInfo.sources.length > 0 && (
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Scientific Sources
                  </Typography>
                  <List dense>
                    {diseaseInfo.sources.map((source) => (
                      <ListItem key={source.id}>
                        <ListItemText 
                          primary={source.name}
                          secondary={`Source ID: ${source.id}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DiseaseModal;
